import { useState } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";

/**
 * Placeholder fields copied from landing-v3-L.pen ("Card de depoimento"
 * and the three cards in "08 Depoimentos"). No name, handle, or post URL
 * is filled in. The design-note under the heading is not rendered.
 */
const EXAMPLE_TAG = "EXEMPLO — placeholder";
const AUTHOR_NAME = "[Nome]";
const AUTHOR_HANDLE = "@[handle]";
const AUTHOR_INITIALS = "[AB]";
const POST_DATE = "[dd/mm/aaaa]";
const PLATFORM_ICON = "[ícone]";
const POST_BODY =
  '[Texto do post — placeholder. O texto aparece exatamente como foi publicado e é cortado em 4 linhas; o restante abre em "ver mais".]';
const POST_BODY_LEAD = "[Texto do post — placeholder.";

const NETWORKS = ["X", "Instagram", "X"] as const;

type ExampleNetwork = (typeof NETWORKS)[number];

function postLinkLabel(network: ExampleNetwork): string {
  return network === "Instagram" ? "Ver no Instagram" : "Ver no X";
}

function avatarColors(network: ExampleNetwork): { background: string; color: string } {
  if (network === "Instagram") {
    return {
      background: "var(--accent-amber-shape-on-light)",
      color: "var(--v3l-text-primary)",
    };
  }
  return {
    background: "var(--v3l-surface-navy-2)",
    color: "var(--v3l-band-navy-heading)",
  };
}

function TestimonialCard({ network, index }: { network: ExampleNetwork; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const bodyId = `landing-testimonial-body-${index}`;
  const avatar = avatarColors(network);

  return (
    <article className="landing-testimonial-card">
      <p className="landing-testimonial-card__tag">{EXAMPLE_TAG}</p>
      <div className="landing-testimonial-card__author">
        <span className="landing-testimonial-card__avatar" style={avatar} aria-hidden="true">
          {AUTHOR_INITIALS}
        </span>
        <div className="landing-testimonial-card__names">
          <p className="landing-testimonial-card__name">{AUTHOR_NAME}</p>
          <p className="landing-testimonial-card__handle">{AUTHOR_HANDLE}</p>
        </div>
        <div className="landing-testimonial-card__platform">
          <span className="landing-testimonial-card__icon">{PLATFORM_ICON}</span>
          <p className="landing-testimonial-card__platform-label">{network}</p>
        </div>
      </div>
      <p id={bodyId} className="landing-testimonial-card__body">
        {expanded ? POST_BODY : POST_BODY_LEAD}
      </p>
      <button
        type="button"
        className="landing-testimonial-card__more"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded(true)}
      >
        ver mais
        <ChevronDown aria-hidden="true" size={16} />
      </button>
      <div className="landing-testimonial-card__footer">
        <p className="landing-testimonial-card__date">{POST_DATE}</p>
        <button type="button" className="landing-testimonial-card__link">
          {postLinkLabel(network)}
          <ExternalLink aria-hidden="true" size={16} />
        </button>
      </div>
    </article>
  );
}

const TESTIMONIAL_CSS = `
.landing-testimonials {
  box-sizing: border-box;
  width: 100%;
  background: var(--v3l-surface-base);
  color: var(--v3l-text-primary);
  font-family: "Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
    "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
  padding: 56px 16px;
}
.landing-testimonials *,
.landing-testimonials *::before,
.landing-testimonials *::after {
  box-sizing: border-box;
}
.landing-testimonials__inner {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}
.landing-testimonials__header {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.landing-testimonials__eyebrow {
  margin: 0;
  color: var(--v3l-text-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1.68px;
  line-height: 1.2857;
}
.landing-testimonials__title {
  margin: 0;
  color: var(--v3l-text-primary);
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.1333;
}
.landing-testimonials__intro {
  margin: 0;
  max-width: 760px;
  color: var(--v3l-text-secondary);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
}
.landing-testimonials__cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.landing-testimonials__cards > li {
  display: flex;
  min-width: 0;
}
.landing-testimonial-card {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  padding: 20px;
  border: 1px solid var(--v3l-border-divider);
  border-radius: 24px;
  background: var(--v3l-surface-base);
  box-shadow: 0 18px 60px var(--card-shadow);
  color: var(--v3l-text-primary);
}
.landing-testimonial-card__tag {
  align-self: flex-start;
  margin: 0;
  border: 1px solid var(--v3l-ph-border);
  border-radius: 6px;
  background: var(--v3l-ph-bg);
  padding: 2px 8px;
  color: var(--v3l-ph-fg);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}
.landing-testimonial-card__author {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.landing-testimonial-card__avatar {
  display: flex;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
}
.landing-testimonial-card__names {
  display: flex;
  min-width: 0;
  flex: 1 1 8rem;
  flex-direction: column;
  gap: 2px;
}
.landing-testimonial-card__name,
.landing-testimonial-card__handle,
.landing-testimonial-card__platform-label,
.landing-testimonial-card__body,
.landing-testimonial-card__date {
  margin: 0;
}
.landing-testimonial-card__name {
  color: var(--v3l-text-primary);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}
.landing-testimonial-card__handle,
.landing-testimonial-card__date {
  color: var(--v3l-text-secondary);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
}
.landing-testimonial-card__platform {
  display: flex;
  width: 96px;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  margin-left: auto;
}
.landing-testimonial-card__icon {
  border: 1px solid var(--v3l-ph-border);
  border-radius: 6px;
  background: var(--v3l-ph-bg);
  padding: 2px 6px;
  color: var(--v3l-ph-fg);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}
.landing-testimonial-card__platform-label {
  color: var(--v3l-text-primary);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}
.landing-testimonial-card__body {
  color: var(--v3l-text-body-strong);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}
.landing-testimonial-card__more,
.landing-testimonial-card__link {
  display: inline-flex;
  height: 44px;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--v3l-text-link);
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.landing-testimonial-card__link {
  gap: 6px;
  max-width: 100%;
  margin-left: auto;
}
.landing-testimonial-card__more:focus-visible,
.landing-testimonial-card__link:focus-visible {
  outline: 2px solid var(--v3l-ui-cta-focus-ring);
  outline-offset: 2px;
}
.landing-testimonial-card__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 12px;
}
@media (min-width: 768px) {
  .landing-testimonials {
    padding: 100px max(24px, calc((100% - 1200px) / 2));
  }
  .landing-testimonials__inner {
    gap: 40px;
  }
  .landing-testimonials__title {
    font-size: 48px;
    letter-spacing: -1.44px;
    line-height: 1.0833;
  }
  .landing-testimonials__intro {
    font-size: 19px;
    line-height: 1.5263;
  }
  .landing-testimonials__cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
  }
}
`;

export default function LandingTestimonials() {
  return (
    <section aria-label="testimonials" className="landing-testimonials">
      <style>{TESTIMONIAL_CSS}</style>
      <div className="landing-testimonials__inner">
        <header className="landing-testimonials__header">
          <p className="landing-testimonials__eyebrow">DEPOIMENTOS</p>
          <h2 className="landing-testimonials__title">O que alunos dizem nas redes</h2>
          <p className="landing-testimonials__intro">
            Publicações de alunos no X e no Instagram, mostradas com autorização de quem postou.
            Cada uma tem link para o post original.
          </p>
        </header>
        <ul className="landing-testimonials__cards">
          {NETWORKS.map((network, index) => (
            <li key={`${network}-${index}`}>
              <TestimonialCard network={network} index={index} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
